import 'dayjs/locale/es';

import { Box } from '@mui/material';
import dayjs from 'dayjs';
import { getTenderDetails, getTenders } from 'lib/api/publicMarket';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import { TENDER_STATES } from 'lib/consts';
import TableWithPagination, {
  Header,
} from 'lib/designSystem/TableWithPagination';
import { errorHandler } from 'lib/functions/errors';
import { Tender, TenderType } from 'lib/types/models';
import { Pagination } from 'lib/types/shared';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showTenderModal } from 'services/redux/tenderModalSlice';

const BASE_PAGINATION: Pagination = {
  page: 1,
  pageSize: 25,
  total: 0,
  totalPages: 0,
};

const Tenders: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Tender[]>([]);
  const [pagination, setPagination] = useState<Pagination>(BASE_PAGINATION);

  const [dateOptions, setDateOptions] = useState<
    'publication' | 'adjudication'
  >('publication');
  const [externalCode, setExternalCode] = useState<string>('');
  const [selectedBuyer, setSelectedBuyer] = useState<string>('');
  const [status, setStatus] = useState<{ value: string; label: string }[]>([]);
  const [types, setTypes] = useState<{ value: TenderType; label: string }[]>(
    [],
  );
  const [initialDateStr, setInitialDateStr] = useState<string | null>(
    dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
  );
  const [finalDateStr, setFinalDateStr] = useState<string | null>(
    dayjs().format('YYYY-MM-DD'),
  );

  const tenderTypeOptions = Object.values(TenderType);

  const statusOptions = TENDER_STATES.map((s) => ({
    value: String(s.value),
    label: s.label,
  }));

  const filters: Filter[] = [
    {
      type: 'date',
      label: 'Fecha inicio',
      dateKey: 'startDate',
      value: initialDateStr,
      setter: setInitialDateStr,
    },
    {
      type: 'date',
      label: 'Fecha fin',
      dateKey: 'endDate',
      value: finalDateStr,
      setter: setFinalDateStr,
    },
    {
      type: 'select',
      label: 'Fecha',
      value: dateOptions,
      setter: setDateOptions,
      options: [
        { value: 'publication', label: 'Publicación' },
        { value: 'adjudication', label: 'Adjudicación' },
      ],
    },
    {
      type: 'text',
      label: 'Código',
      value: externalCode,
      setter: setExternalCode,
    },
    {
      type: 'multiple',
      label: 'Tipo',
      value: types,
      setter: setTypes,
      options: tenderTypeOptions.map((t) => ({ value: t, label: t })),
    },
    {
      type: 'autocomplete',
      label: 'Mandante',
      key: 'buyer',
      value: selectedBuyer,
      setter: setSelectedBuyer,
    },
    {
      type: 'multiple',
      label: 'Estado',
      value: status,
      setter: setStatus,
      options: statusOptions,
    },
  ];

  const buildTendersQuery = (override?: {
    page?: number;
    pageSize?: number;
  }): string => {
    const params = new URLSearchParams();
    if (externalCode) params.append('externalCode', externalCode);
    if (selectedBuyer) params.append('buyerId', selectedBuyer);
    if (types.length > 0)
      params.append('types', types.map((t) => t.value).join(','));

    const shouldUseDateOptions = true;

    if (initialDateStr && shouldUseDateOptions)
      params.append(
        dateOptions === 'publication'
          ? 'startPublicationDate'
          : 'startAdjudicationDate',
        initialDateStr,
      );

    if (finalDateStr && shouldUseDateOptions)
      params.append(
        dateOptions === 'publication'
          ? 'endPublicationDate'
          : 'endAdjudicationDate',
        finalDateStr,
      );

    if (status.length > 0)
      params.append('status', status.map((s) => s.value).join(','));
    const pageToUse = override?.page ?? pagination.page;
    const pageSizeToUse = override?.pageSize ?? pagination.pageSize;
    if (pageToUse) params.append('page', String(pageToUse));
    if (pageSizeToUse) params.append('pageSize', String(pageSizeToUse));
    return params.toString();
  };

  const fetchData = async (params?: {
    page: number;
    pageSize: number;
  }): Promise<{ data: Tender[]; pagination: Pagination } | void> => {
    try {
      setLoading(true);
      const pageToUse = params?.page ?? pagination.page;
      const pageSizeToUse = params?.pageSize ?? pagination.pageSize;
      const queryString = buildTendersQuery({
        page: pageToUse,
        pageSize: pageSizeToUse,
      });
      const response = await getTenders(dispatch, queryString);
      const nextData = (response?.data as Tender[]) ?? [];
      const nextPagination = response?.pagination ?? BASE_PAGINATION;
      setData(nextData);
      setPagination(nextPagination);
      return {
        data: nextData,
        pagination: nextPagination,
      };
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    await fetchData({
      page: BASE_PAGINATION.page,
      pageSize: BASE_PAGINATION.pageSize,
    });
  };

  useEffect(() => {
    void fetchData({
      page: BASE_PAGINATION.page,
      pageSize: BASE_PAGINATION.pageSize,
    });
  }, []);

  const headers: Header[] = [
    {
      label: 'Código',
      key: 'externalCode',
      customComponent: (value: string) => <Box sx={{ py: 1 }}>{value}</Box>,
    },
    { label: 'Nombre', key: 'name' },
    { label: 'Tipo', key: 'type' },
    { label: 'Estado', key: 'status' },
    { label: 'Mandante', key: 'buyerId' },
    { label: 'Fecha Publicación', key: 'publicationDate', type: 'date' },
    { label: 'Fecha Cierre', key: 'closeDate', type: 'date' },
    { label: 'Fecha Adjudicación', key: 'adjudicationDate', type: 'date' },
    { label: 'Monto Estimado', key: 'estimatedAmount', type: 'money' },
  ];

  const handleRowClick = async (rowId: string) => {
    try {
      if (!rowId) throw new Error('Tender ID is required');
      const tenderDetails = await getTenderDetails(dispatch, rowId);
      if (tenderDetails) dispatch(showTenderModal(tenderDetails));
    } catch (error) {
      errorHandler(dispatch, error);
    }
  };

  return (
    <Box sx={{ position: 'relative', py: 2 }}>
      <Box sx={{ position: 'absolute', top: -36, right: 0 }}>
        <GlobalSearcher
          filters={filters}
          triggerSearch={handleSearch}
          dispatch={dispatch}
        />
      </Box>
      <Box sx={{ pt: 2 }}>
        <TableWithPagination
          data={data}
          setData={setData}
          dataHeaders={headers}
          loading={loading}
          handleClickRow={handleRowClick}
          pagination={pagination}
          setPagination={setPagination}
          searchData={fetchData}
          exportToFile="xlsx"
        />
      </Box>
    </Box>
  );
};

export default Tenders;
